import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '@playerbase/ui';
import type { GamePlatform } from '@playerbase/types';

import { useShell } from '../shell/use-shell.js';
import { createGame } from '../games/game-data.js';
import { PlatformToggle } from './game-settings/platform-toggle.js';

export function NewGamePage(): JSX.Element {
  const { activeStudio, games, refresh } = useShell();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [platforms, setPlatforms] = useState<GamePlatform[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePlatform = useCallback((platform: GamePlatform): void => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform],
    );
  }, []);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('Game name is required.');
      return;
    }

    const duplicate = games.find(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate !== undefined) {
      const ok = window.confirm(
        `Another game on this studio is already called "${duplicate.name}". Create anyway?`,
      );
      if (!ok) return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const created = await createGame({
        studioId: activeStudio.id,
        name: trimmed,
        targetPlatforms: platforms,
      });
      await refresh();
      // Land on the post-creation Discord prompt rather than straight on
      // game-settings/overview - the story calls for the Connect a Discord
      // server CTA to be the first thing a freshly-created game sees, with
      // Connect later as an explicit skip.
      navigate(`/s/${activeStudio.shortId}/g/${created.shortId}/welcome-discord`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Could not create the game: ${message}`);
    } finally {
      setSubmitting(false);
    }
  }, [activeStudio.id, activeStudio.shortId, games, name, navigate, platforms, refresh]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <header className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-wider text-text-muted">
          {activeStudio.name}
        </p>
        <h1 className="mt-2 font-display text-2xl font-bold text-text">
          Set up a game
        </h1>
        <p className="mt-2 text-sm text-text-muted">
          Give the game a name and pick the platforms it ships to. You can change
          either later from Game settings → Game overview.
        </p>
      </header>

      <div className="space-y-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-game-name">Game name</Label>
          <Input
            id="new-game-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hollow Knight"
            autoFocus
          />
        </div>

        <div>
          <Label>Platforms</Label>
          <p className="mt-1 mb-2 text-xs text-text-muted">
            Optional now - you can add platforms once the game exists.
          </p>
          <PlatformToggle
            selected={platforms}
            onToggle={togglePlatform}
            disabled={submitting}
          />
        </div>

        {error !== null && (
          <p role="alert" className="text-sm text-danger-text">
            {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="md"
            disabled={submitting || name.trim().length === 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Creating…' : 'Create game'}
          </Button>
          <Button asChild variant="ghost" size="md">
            <Link to={`/s/${activeStudio.shortId}`}>Cancel</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
